import { dayOf } from '@day';
import { useStore } from '../app/store';
import { importSheet, openShare, openWaiting, planWith, ui } from '../app/ui';
import * as act from '../app/actions';
import { openDialog } from '../app/dialogs';
import { dayWords, money, plural, time } from '../app/words';
import type { Model } from '../data/model';
import { readyToPlan, spotOf, type Waiting } from '../data/waiting';
import { whoMay } from '../app/sharing';
import { shortCode } from '../data/pins';
import { EmptyHelp, More, nearly } from '../ui/bits';
import { Icon } from '../ui/Icon';
import { copyText } from './StopBody';
import { noTextPick, pick, rowClick } from '../app/select';

/**
 * Stops waiting for a route (docs/waiting-stops.md): booked before their route exists, typed in or read from a sheet,
 * then planned onto one. Search, sort, filters for the day and where they came from; select several to set their day,
 * remove them or plan a route from them. Right-click and ⌘K carry the same actions (menus.ts `waitingMenu`).
 */

const COLS: { k: string; l: string; low?: boolean; sort: (w: Waiting) => string | number }[] = [
  { k: 'addr', l: 'Address', sort: (w) => w.street.toLowerCase() },
  { k: 'who', l: 'Customer', sort: (w) => (w.name ?? '~').toLowerCase() },
  { k: 'day', l: 'Day', sort: (w) => w.day ?? '9999' },
  { k: 'from', l: 'From', low: true, sort: (w) => (w.sheet ? `${w.sheet.file} ${String(w.sheet.row).padStart(5, '0')}` : '~') },
  { k: 'added', l: 'Added', low: true, sort: (w) => -(w.addedAt ?? Date.now()) },
];

/** "today", "tomorrow", "Tuesday 3 November", "No day". */
export const dayLabel = (m: Model, day: string | null) => (day ? act.dayName(m, day).replace(/^./, (c) => c.toUpperCase()) : 'No day');

/** Where a waiting stop came from, in a few words. */
export const fromLabel = (w: Waiting) => (w.sheet ? `${w.sheet.file}, row ${w.sheet.row}` : w.source === 'sheet' ? 'A sheet' : 'Typed in');

export function waitingRows(m: Model, s: { q: string; wsort: { k: string; dir: 1 | -1 } | null; wday: string | null; wsrc: string | null }) {
  const q = s.q.trim().toLowerCase();
  let rows = m.waiting.filter(
    (w) =>
      (!s.wday || (s.wday === 'none' ? !w.day : w.day === s.wday)) &&
      (!s.wsrc || (s.wsrc === 'typed' ? w.source === 'typed' : w.sheet?.file === s.wsrc.slice(6))) &&
      (!q || [w.address, w.stop.writtenAddress, w.name, w.phone, w.notes, w.sheet?.file].join(' ').toLowerCase().includes(q)),
  );
  if (s.wsort) {
    const col = COLS.find((c) => c.k === s.wsort!.k) ?? COLS[0];
    rows = [...rows].sort((a, b) => {
      const x = col.sort(a);
      const y = col.sort(b);
      return (x < y ? -1 : x > y ? 1 : 0) * s.wsort!.dir;
    });
  }
  return rows;
}

export function WaitingPage({ m }: { m: Model }) {
  const s = useStore(ui);
  const rows = waitingRows(m, s);
  const ids = rows.map((w) => w.id);
  const sel = ids.filter((id) => s.sel.has(id));
  const all = ids.length > 0 && sel.length === ids.length;
  const planned = new Set(s.plan?.stops.map((x) => x.waitingId).filter(Boolean));
  const cols = COLS;
  const q = s.q.trim();
  const sortBy = (k: string) => ui.set({ wsort: s.wsort?.k !== k ? { k, dir: 1 } : s.wsort.dir === 1 ? { k, dir: -1 } : null });
  const filtered = !!(s.wday || s.wsrc);
  return (
    <div className="card">
      <div className="tools">
        {sel.length ? (
          <>
            <b>{plural(sel.length, 'stop')} selected</b>
            <button className="btn sm primary" onClick={() => void act.planFromWaiting(sel)}>
              <Icon name="path" size="sm" />
              Plan a route
            </button>
            <button className="btn sm" data-menu={`wadd:${sel.join(',')}`}>
              <Icon name="plus" size="sm" />
              Add to a route
              <Icon name="caretDown" size="sm" />
            </button>
            <button className="btn sm" disabled={!m.people.length} data-tip={m.people.length ? 'Across your couriers, on Live beside the map' : 'Invite a courier first'} onClick={() => openShare(sel, m.people.map((p) => p.uid))}>
              <Icon name="arrowsSplit" size="sm" />
              Share out
            </button>
            <button className="btn sm" data-menu={`wsetday:${sel.join(',')}`}>
              <Icon name="calendarBlank" size="sm" />
              Set the day
              <Icon name="caretDown" size="sm" />
            </button>
            <button className="btn sm" data-menu={`wchange:${sel.join(',')}`} data-tip="Delivery or pickup, time at the stop, deliver by, who may take them">
              <Icon name="notePencil" size="sm" />
              Change
              <Icon name="caretDown" size="sm" />
            </button>
            <button className="btn sm danger" onClick={() => act.removeWaiting(sel)}>
              <Icon name="trash" size="sm" />
              Remove
            </button>
            <button className="btn sm" onClick={() => ui.set({ sel: new Set() })}>
              Clear the selection
            </button>
          </>
        ) : (
          <>
            <button className="btn sm strong" onClick={() => openDialog({ kind: 'addWaiting' })}>
              <Icon name="plus" size="sm" />
              Add a stop
            </button>
            <button className="btn sm" onClick={importSheet}>
              <Icon name="table" size="sm" />
              Import a sheet
            </button>
            {s.wday && (
              <span className="chip fchip">
                <Icon name="calendarBlank" size="sm" />
                {s.wday === 'none' ? 'No day' : dayLabel(m, s.wday)}
                <button data-tip="Any day" aria-label="Any day" onClick={() => ui.set({ wday: null, sel: new Set() })}>
                  <Icon name="x" size="sm" />
                </button>
              </span>
            )}
            {s.wsrc && (
              <span className="chip fchip">
                <Icon name={s.wsrc === 'typed' ? 'notePencil' : 'table'} size="sm" />
                {s.wsrc === 'typed' ? 'Typed in' : s.wsrc.slice(6)}
                <button data-tip="From anywhere" aria-label="From anywhere" onClick={() => ui.set({ wsrc: null, sel: new Set() })}>
                  <Icon name="x" size="sm" />
                </button>
              </span>
            )}
            <button className="ctl sm" data-menu="wday:">
              <Icon name="calendarBlank" />
              Day
              <Icon name="caretDown" size="sm" />
            </button>
            <button className="ctl sm" data-menu="wsrc:">
              <Icon name="funnelSimple" />
              From
              <Icon name="caretDown" size="sm" />
            </button>
            <div className="field">
              <Icon name="magnifyingGlass" />
              <input id="stopq" placeholder="Search street, customer, phone, note or sheet" value={s.q} onChange={(e) => ui.set({ q: e.target.value })} autoComplete="off" spellCheck={false} aria-label="Search waiting stops" />
            </div>
          </>
        )}
        <span className="grow" />
        <span className="cap">
          {rows.length} of {m.waiting.length}
        </span>
      </div>
      {m.data.waitingUnreadable && (
        <div className="empty cap">Stops waiting for a route can’t be read yet: Routr’s server needs an update first.</div>
      )}
      <div className="tw">
        <table>
          <thead>
            <tr>
              <th className="cb">
                <button
                  className={`cbx${all ? ' on' : ''}`}
                  role="checkbox"
                  aria-checked={all}
                  aria-label={all ? 'Clear these' : 'Select these'}
                  data-tip={all ? 'Clear these' : 'Select these'}
                  onClick={() => ui.set((x) => ({ sel: all ? new Set([...x.sel].filter((k) => !ids.includes(k))) : new Set([...x.sel, ...ids]) }))}
                >
                  {all && <Icon name="check" />}
                </button>
              </th>
              {cols.map((c) => (
                <th
                  key={c.k}
                  className={c.low ? 'low' : ''}
                  data-sortcol={c.k}
                  tabIndex={0}
                  aria-sort={s.wsort?.k === c.k ? (s.wsort.dir > 0 ? 'ascending' : 'descending') : 'none'}
                  onClick={() => sortBy(c.k)}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), sortBy(c.k))}
                >
                  {c.l}
                  {s.wsort?.k === c.k && <Icon name={s.wsort.dir > 0 ? 'caretUp' : 'caretDown'} size="sm" />}
                </th>
              ))}
              <th className="act" />
            </tr>
          </thead>
          <tbody>
            {rows.length ? (
              rows.map((w) => {
                const on = s.sel.has(w.id);
                return (
                  <tr key={w.id} className={`r${s.rec?.type === 'waiting' && s.rec.id === w.id ? ' sel' : ''}${on ? ' on' : ''}`} data-waiting={w.id} data-ctx={`waiting:${w.id}`} onMouseDown={noTextPick} onClick={(e) => rowClick(e, ids, w.id, () => openWaiting(w.id))}>
                    <td className="cb" onClick={(e) => e.stopPropagation()}>
                      <button className={`cbx${on ? ' on' : ''}`} role="checkbox" aria-checked={on} aria-label={`Select ${w.street}`} onClick={(e) => pick(ids, w.id, e.shiftKey)}>
                        {on && <Icon name="check" />}
                      </button>
                    </td>
                    <td className="ad">
                      {w.notes && <span className="nmark" data-tip={`Note: ${w.notes}`} />}
                      <div className="addr">{w.street}</div>
                      <div className="area">
                        {[w.area, w.type === 'pickup' ? 'Pickup' : null, w.packages > 1 ? `${w.packages} packages` : null, w.collect ? `${money(w.collect, w.currency ?? m.currency)} to collect` : null, w.couriers ? `Only ${whoMay(m, w.couriers)}` : null].filter(Boolean).join(' · ') || ' '}
                      </div>
                    </td>
                    <td className="nw">
                      {w.name ?? <span className="ink2">–</span>}
                      {w.phone && <div className="area">{w.phone}</div>}
                    </td>
                    <td className="nw">
                      {planned.has(w.id) ? (
                        <span className="chip" data-tip="In the route being planned on Live">
                          <Icon name="path" size="sm" />
                          In the new route
                        </span>
                      ) : w.day ? (
                        <span className={`chip${w.day < m.today ? ' cau' : ''}`} data-tip={w.day < m.today ? 'That day has gone' : undefined}>
                          {dayLabel(m, w.day)}
                        </span>
                      ) : (
                        <span className="ink2">–</span>
                      )}
                    </td>
                    <td className="nw low">
                      <span className="cap">{fromLabel(w)}</span>
                    </td>
                    <td className="nw low">
                      <span className="cap">{w.addedAt == null ? 'Just now' : w.addedAt >= m.todayStart ? time(w.addedAt, m.zone) : dayWords(dayOf(w.addedAt, m.zone), { weekday: false })}</span>
                    </td>
                    <td className="act" onClick={(e) => e.stopPropagation()}>
                      <More menu={`waiting:${w.id}`} />
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={cols.length + 2}>
                  {m.waiting.length === 0 && !q ? (
                    <EmptyHelp
                      title="No stops waiting for a route."
                      tries={[
                        { icon: 'plus', label: 'Add a stop', note: 'Book it now, with the day it’s for; plan its route when you’re ready', act: 'add' },
                        { icon: 'table', label: 'Import a sheet', note: 'A CSV or Excel file, one row a stop', act: 'import' },
                        { icon: 'path', label: 'Plan a route', note: 'Straight onto a route, for a courier', act: 'plan' },
                      ]}
                      onTry={(a) => (a === 'add' ? openDialog({ kind: 'addWaiting' }) : a === 'import' ? importSheet() : planWith())}
                    />
                  ) : (
                    <EmptyHelp
                      q={q}
                      title={`No stops waiting${s.wday ? ` for ${s.wday === 'none' ? 'no particular day' : act.dayName(m, s.wday)}` : ''}${s.wsrc ? ` from ${s.wsrc === 'typed' ? 'typing' : s.wsrc.slice(6)}` : ''}.`}
                      tries={[
                        ...nearly(q, m.waiting, (w) => `${w.address} ${w.name ?? ''}`).map((w) => ({ icon: 'mapPin', label: w.street, note: w.name ?? undefined, act: `w:${w.id}` })),
                        ...(filtered ? [{ icon: 'funnelSimple', label: 'Take the filters off', note: 'Every day, from anywhere', act: 'all' }] : []),
                        { icon: 'stack', label: 'Today’s stops on routes', note: 'All stops, by status', act: 'routes' },
                        ...(q ? [{ icon: 'x', label: 'Clear the search', act: 'clear' }] : [{ icon: 'plus', label: 'Add a stop', act: 'add' }]),
                      ]}
                      onTry={(a) => {
                        if (a.startsWith('w:')) return openWaiting(a.slice(2));
                        if (a === 'all') return ui.set({ wday: null, wsrc: null, sel: new Set() });
                        if (a === 'routes') return ui.set({ tab: 'all', sel: new Set() });
                        if (a === 'clear') return ui.set({ q: '' });
                        if (a === 'add') return openDialog({ kind: 'addWaiting' });
                      }}
                    />
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** A waiting stop's record, docked beside the table. */
export function WaitingRecord({ m, w, close }: { m: Model; w: Waiting; close: () => void }) {
  const spot = spotOf(w);
  const ready = readyToPlan(w, m.now);
  const where = w.pin
    ? `The customer’s own pin · ${shortCode(w.pin.lat, w.pin.lng)}`
    : spot
      ? ready
        ? 'Found by Google today: it goes onto a route as it is'
        : 'Found by Google: looked up again when it’s planned'
      : 'Not on the map now: looked up when it’s planned';
  const kv = (k: string, v: React.ReactNode) =>
    v ? (
      <div className="kv">
        <span className="k">{k}</span>
        <span className="v">{v}</span>
        <span />
      </div>
    ) : null;
  return (
    <div>
      <div className="stophead">
        <div className="row">
          <div className="grow" style={{ minWidth: 0 }}>
            <div className="eyebrow">Waiting for a route</div>
            <div className="st">{w.street}</div>
            <div className="cap">{w.area}</div>
          </div>
          <button className="iconbtn sm" data-tip="Copy the address" aria-label="Copy the address" onClick={() => copyText(String(w.stop.writtenAddress ?? w.address), 'Address copied')}>
            <Icon name="copy" />
          </button>
          <More menu={`waiting:${w.id}`} />
          <button className="iconbtn sm" data-tip="Close · Esc" aria-label="Close" onClick={close}>
            <Icon name="x" />
          </button>
        </div>
        <div style={{ height: 10 }} />
      </div>
      <div className="rec-b">
        <div className="acts" style={{ marginTop: 0 }}>
          <button className="mini strong" onClick={() => void act.planFromWaiting([w.id])}>
            <Icon name="path" size="sm" />
            Plan a route with it
          </button>
          <button className="mini" data-menu={`wadd:${w.id}`}>
            <Icon name="plus" size="sm" />
            Add to a route
          </button>
          <button className="mini" data-menu={`wsetday:${w.id}`}>
            <Icon name="calendarBlank" size="sm" />
            {w.day ? dayLabel(m, w.day) : 'Set the day'}
          </button>
          <button className="mini" data-menu={`wallow:${w.id}`}>
            <Icon name="users" size="sm" />
            {w.couriers ? `Only ${whoMay(m, w.couriers)}` : 'Who may take it'}
          </button>
          <button className="mini" onClick={() => act.removeWaiting([w.id])}>
            <Icon name="trash" size="sm" />
            Remove
          </button>
        </div>
        <div className="kvh eyebrow" style={{ marginTop: 16 }}>
          The stop
        </div>
        {kv('Address', String(w.stop.writtenAddress ?? w.address))}
        {kv('Customer', w.name)}
        {kv('Phone', w.phone)}
        {kv('Type', w.type === 'pickup' ? 'Pickup' : null)}
        {kv('Packages', w.packages > 1 ? String(w.packages) : null)}
        {kv('To collect', w.collect ? money(w.collect, w.currency ?? m.currency) : null)}
        {kv('Note', w.notes)}
        {kv('Day', w.day ? dayLabel(m, w.day) : 'Any day')}
        {kv('Who may take it', w.couriers ? `Only ${whoMay(m, w.couriers)}` : 'Any courier')}
        {kv('On the map', where)}
        <div className="kvh eyebrow">Where it came from</div>
        {kv('From', w.sheet ? `Row ${w.sheet.row} of ${w.sheet.file}` : w.source === 'sheet' ? 'A sheet' : 'Typed in on Routr Dispatch')}
        {kv('Added', w.addedAt != null ? `${w.addedAt >= m.todayStart ? 'Today' : dayWords(dayOf(w.addedAt, m.zone))}, ${time(w.addedAt, m.zone)}` : 'Just now')}
        {kv('Kept until', w.expiresAt != null ? `${dayWords(dayOf(w.expiresAt, m.zone))}, unless it’s planned before` : null)}
      </div>
    </div>
  );
}
